/* eslint-disable no-underscore-dangle */
import {
    ModelType,
    SearchableRecordInstantSearch,
    SearchableAnalysisInDecision,
    SearchableDecisionInstantSearch,
} from '@livv/models';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Hit } from 'instantsearch.js';
import { ReactElement } from 'react';

const addSeparator = (
    path: string[],
    addEllipsis: boolean = false,
    highlightedPath: string[] | null = null,
): JSX.Element[] =>
    path.reduce((completedPath: ReactElement[], currentNode, index) => {
        const separator = (
            <ChevronRightIcon
                key={`chevron-${currentNode}`}
                fontSize="inherit"
                sx={{ verticalAlign: 'middle' }}
            />
        );

        // Check if a highlight is available
        const highlightedValue = highlightedPath ? highlightedPath[index] : null;

        const nodeElement = highlightedValue ? (
            // eslint-disable-next-line react/no-danger
            <span dangerouslySetInnerHTML={{ __html: highlightedValue }} key={currentNode} />
        ) : (
            <span key={currentNode}>{currentNode}</span>
        );

        if (index !== path.length - 1) {
            return [...completedPath, nodeElement, separator];
        }

        if (addEllipsis && index === path.length - 1) {
            const ellipsis = <span key="ellipsis">[…]</span>;

            return [...completedPath, nodeElement, separator, ellipsis];
        }

        return [...completedPath, nodeElement];
    }, []);

interface RecordWithDynamicKeys {
    [key: string]: string | undefined;
}

const getKeyWordHierarchyLevel = (
    record: RecordWithDynamicKeys,
    keywordPrefix: string = 'keywordHierarchy.lvl',
): string[] => {
    let keywordHierarchyLevel = 0;

    while (record[`${keywordPrefix}${keywordHierarchyLevel}`] !== undefined) {
        keywordHierarchyLevel++;
    }

    return keywordHierarchyLevel
        ? (record[`${keywordPrefix}${keywordHierarchyLevel - 1}`] as string).split(' > ')
        : [];
};

const getAnalysisPath = (
    analysis: SearchableAnalysisInDecision,
    maxAnalysisPathItems: number,
): string[] => {
    const { pathKeywords } = analysis;

    if (pathKeywords.length > maxAnalysisPathItems) {
        return pathKeywords.slice(0, maxAnalysisPathItems);
    }

    return pathKeywords;
};

const getPath = <T extends SearchableRecordInstantSearch>(record: T): string[] =>
    getKeyWordHierarchyLevel(record as RecordWithDynamicKeys);

const getPaths = <T extends SearchableRecordInstantSearch>(record: T): string[][] =>
    (record.pathKeywords as string[][]).map((path, index) => [
        (record['keywordHierarchy.lvl0'] as string[])[index],
        ...path,
    ]);

export const getDisplayablePathItems = <T extends SearchableRecordInstantSearch>(
    record: T,
): JSX.Element[][] => {
    const { modelType } = record;

    switch (modelType) {
        case ModelType.PUBLICATION_SHEET:
            return [
                addSeparator(
                    getPath(record),
                    false,
                    (
                        record._highlightResult as { pathTitles: { value: string }[] }
                    )?.pathTitles?.map((p) => p.value) || null,
                ),
            ];
        case ModelType.GLOSSARY_TERM:
            return getPaths(record).map((path, index) =>
                addSeparator(
                    path,
                    false,
                    (
                        record._highlightResult as { pathKeywords: { value: string }[][] }
                    )?.pathKeywords?.[index]?.map((p) => p.value) || null,
                ),
            );
        default:
            return [];
    }
};

export const getDisplayableAnalysesInDecisionPathItems = (
    analysis: SearchableAnalysisInDecision,
    hit: Hit<SearchableDecisionInstantSearch>,
    maxAnalysisPathItems: number,
): JSX.Element[][] => [
    addSeparator(
        getAnalysisPath(analysis, maxAnalysisPathItems),
        analysis.pathKeywords.length > maxAnalysisPathItems,
        (hit._highlightResult?.analyses as { pathKeywords?: { value: string }[] }[])?.[
            hit.analyses.indexOf(analysis)
        ]?.pathKeywords?.map((p) => p.value) || null,
    ),
];

export const extractSpecificFilter = (allFilters: string, regex: RegExp): string[] => {
    const matches = [];
    let match;

    // eslint-disable-next-line no-cond-assign
    while ((match = regex.exec(allFilters)) !== null) {
        matches.push(match[1]);
    }

    return matches;
};
